import React from 'react';
import Layout from '@/components/Layout';
import { BookOpen, Lightbulb, Wrench, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const CommunityPage = () => {
  const articles = [
    {
      icon: Lightbulb,
      title: "Choosing the Right Laptop in 2025",
      excerpt: "Not sure which laptop fits your needs? Learn about processors, RAM, storage, and how to match specs to your workflow.",
      readTime: "5 min read"
    },
    {
      icon: Wrench,
      title: "Laptop Maintenance Tips",
      excerpt: "Simple practices to extend your laptop's life: cleaning, battery care, software updates, and safe storage.",
      readTime: "4 min read"
    },
    {
      icon: TrendingUp,
      title: "Refurbished vs New: What's Best for You?",
      excerpt: "Discover the benefits of refurbished laptops and when new makes more sense. Quality, warranties, and value explained.",
      readTime: "6 min read"
    },
    {
      icon: BookOpen,
      title: "Setting Up Your New Device",
      excerpt: "Step-by-step guide to unboxing, initial setup, essential software, and security configurations.",
      readTime: "7 min read"
    }
  ];

  return (
    <Layout>
      <div style={{ background: 'var(--color-background)' }}>
        {/* Hero Section */}
        <section className="section-padding" style={{ background: 'var(--color-background-secondary)' }}>
          <div className="container-custom text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
              Bamo Tech Sphere Community
            </h1>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Your trusted resource for tech tips, product guides, and expert advice.
              Stay informed and make smarter technology decisions.
            </p>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="section-padding">
          <div className="container-custom">
            <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: 'var(--color-text-primary)' }}>
              Latest Guides & Tips
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {articles.map((article, idx) => {
                const Icon = article.icon;
                return (
                  <Card key={idx} className="luxury-card cursor-pointer">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-6">
                        <div
                          className="h-16 w-16 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: 'var(--color-accent)', color: 'var(--color-text-on-accent)' }}
                        >
                          <Icon size={32} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--color-text-primary)' }}>
                            {article.title}
                          </h3>
                          <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                            {article.excerpt}
                          </p>
                          <span className="text-sm" style={{ color: 'var(--color-accent)' }}>
                            {article.readTime}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tech Tips Section */}
        <section className="section-padding" style={{ background: 'var(--color-background-secondary)' }}>
          <div className="container-custom">
            <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: 'var(--color-text-primary)' }}>
              Quick Tech Tips
            </h2>
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="p-6 rounded-xl" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-accent)' }}>
                  🔋 Battery Health
                </h3>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Keep your laptop battery between 20-80% charge for optimal longevity. Avoid leaving it plugged in at 100% for extended periods.
                </p>
              </div>
              <div className="p-6 rounded-xl" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-accent)' }}>
                  🧹 Regular Cleaning
                </h3>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Clean your laptop keyboard and screen with a microfiber cloth monthly. Use compressed air for keyboard crevices.
                </p>
              </div>
              <div className="p-6 rounded-xl" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-accent)' }}>
                  💾 Backup Your Data
                </h3>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Use cloud storage or external drives to backup important files weekly. The 3-2-1 rule: 3 copies, 2 different media, 1 offsite.
                </p>
              </div>
              <div className="p-6 rounded-xl" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-accent)' }}>
                  🔒 Security Updates
                </h3>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Enable automatic updates for your operating system and antivirus software. Security patches protect against new threats.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="section-padding">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
              Need More Help?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Our tech experts are here to answer your questions and provide personalized recommendations.
            </p>
            <a href="/contact">
              <button className="luxury-button-primary" data-testid="contact-support-button">
                Contact Support
              </button>
            </a>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CommunityPage;